import React from 'react';
import PropTypes from 'prop-types';
import { RenameInput } from './shared/rename-input';
import { ActionCreators } from '../redux/action-creators';
import { getToken } from '../utils/get-token';
import { coordinateDifference } from '../utils/coordinate-difference';
import ace from 'brace';
import Icon from '@mapbox/mr-ui/icon';
import { sortableHandle } from 'react-sortable-hoc';
import stringify from 'json-stringify-pretty-compact';

import 'brace/mode/json';

class Feature extends React.PureComponent {
  saved = null;
  activeContainerRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      updateStatus: null,
      syntaxErrors: null,
      positionSet: false,
      hovered: false,
      toggled: false
    };
  }

  componentDidUpdate(prevProps) {
    const { positionSet, updateStatus } = this.state;
    const { active, parentContainer, mapResult } = this.props;

    if (
      positionSet &&
      coordinateDifference(
        prevProps.mapResult.geocode.center,
        mapResult.geocode.center
      )
    ) {
      this.setState({ positionSet: false });
    }

    if (active && this.activeContainerRef.current) {
      parentContainer.scrollTop =
        this.activeContainerRef.current.offsetTop - 380;
    }

    if (updateStatus === 'saving') {
      this.saved = setTimeout(() => {
        this.setState({ updateStatus: 'saved' });
        this.saved = null;
      }, 1000);
    }
  }

  updateStatus = ln => {
    ln = ln || 500;
    const { updateStatus } = this.state;

    // Cancel any queued saved status
    if (this.saved) {
      clearTimeout(this.saved);
    }

    if (updateStatus !== 'saving') {
      setTimeout(() => {
        this.setState({ updateStatus: 'saving' });
      }, ln);
    }
  };

  setActive = () => {
    const { feature } = this.props;
    ActionCreators.activeFeature(feature);
  };

  deleteFeature = e => {
    e.stopPropagation();
    const { feature } = this.props;
    ActionCreators.deleteFeature(feature.properties.__id);
  };

  onChangePlaceName = value => {
    const { feature } = this.props;
    this.updateStatus();
    ActionCreators.setPlaceName(feature.properties.__id, value);
  };

  onChangePlaceDescription = e => {
    const value = e.target.value;
    const { feature } = this.props;
    this.updateStatus();
    ActionCreators.setPlaceDescription(feature.properties.__id, value);
  };

  onChangeTags = e => {
    const value = e.target.value;
    const { feature } = this.props;
    const tags = value
      .trim()
      .replace(/\s*,\s*/g, ',')
      .split(',');
    this.updateStatus();
    ActionCreators.setTags(feature.properties.__id, tags);
  };

  toggleAdditional = () => {
    this.setState(state => ({
      toggled: !state.toggled
    }));
  };

  onMouseEnter = () => {
    this.setState({ hovered: true });
  };

  onMouseLeave = () => {
    this.setState({ hovered: false });
  };

  onKeyDown = e => {
    switch (e.keyCode) {
      case 27: // ESC
      case 13: // RETURN
        e.target.blur();
        break;
      default:
        return;
    }
  };

  setPosition = () => {
    const { feature } = this.props;
    this.updateStatus(0);
    this.setState({ positionSet: true }, () => {
      ActionCreators.setPosition(feature.properties.__id);
    });
  };

  setMetaContainer = el => {
    const { feature } = this.props;
    const { toggled } = this.state;
    const metaFields = {};

    // Exclude property fields that are application dependent (like `id`) or
    // already have inputs and are default to the Gazetteer spec.
    Object.keys(feature.properties).filter(key => {
      const specKeys = [
        '__id',
        'place_name',
        'place_description',
        'zoom',
        'tags'
      ];
      if (specKeys.indexOf(key) === -1) {
        metaFields[key] = feature.properties[key];
      }
    });

    if (toggled) {
      this.editor = ace.edit(el);
      this.editor.renderer.setShowGutter(false);
      this.editor.setHighlightActiveLine(false);
      this.editor.session.setOptions({
        mode: 'ace/mode/json',
        tabSize: 2,
        useSoftTabs: true
      });

      const initValue = stringify(metaFields);
      this.editor.setValue(initValue, -1);
      this.editor.session.on('changeAnnotation', () => {
        if (initValue === this.editor.getValue()) return;
        const syntaxErrors = this.editor.session.getAnnotations();
        this.setState({ syntaxErrors }, () => {
          if (!syntaxErrors.length) {
            const value = this.editor.getValue();
            if (!value) return;
            this.updateStatus();
            ActionCreators.setMeta(feature.properties.__id, JSON.parse(value));
          }
        });
      });
    }
  };

  renderStatus() {
    const { updateStatus } = this.state;
    let status;

    if (updateStatus === 'saving') {
      status = (
        <div className="flex-parent txt-s color-gray inline-block">
          <span className="flex-child ml6">Saving…</span>
        </div>
      );
    } else if (updateStatus === 'saved') {
      status = (
        <div className="txt-s color-green">
          <Icon name="check" inline={true} />
          <span className="ml3">{updateStatus}</span>
        </div>
      );
    }

    return status;
  }

  render() {
    const { feature, active } = this.props;
    const { zoom, place_name, place_description, tags } = feature.properties;
    const { coordinates } = feature.geometry;
    const { toggled, hovered, syntaxErrors, positionSet } = this.state;
    const DragHandle = sortableHandle(() => {
      return (
        <div
          style={{ cursor: 'grab' }}
          title="Reorder feature"
          className="flex-child link link--gray mr3"
        >
          <Icon name="menu" inline={true} />
        </div>
      );
    });

    const updatePositionProps = {
      test: 'set-position',
      className: 'flex-child txt-s link'
    };

    if (positionSet) {
      updatePositionProps.disabled = true;
    } else {
      updatePositionProps.onClick = this.setPosition;
    }

    const errors =
      syntaxErrors && syntaxErrors.length
        ? syntaxErrors.map(e => e.text)
        : null;
    const thumbnail = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${
      coordinates[0]
    },${
      coordinates[1]
    },${zoom}/30x30?logo=false&attribution=false&access_token=${getToken()}`;

    let content = (
      <div
        test="feature-container"
        tabIndex="0"
        role="button"
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onClick={this.setActive}
        className="bg-white bg-gray-faint-on-hover cursor-pointer transition flex-parent flex-parent--center-cross py6 px12 border-b border--gray-light"
      >
        <div className="flex-child flex-child--no-shrink w30 h30 scroll-hidden round mr12">
          <img src={thumbnail} />
        </div>
        <div className="flex-child flex-child--grow mr6">
          <h4 title={place_name} className="flex-child wmax300 txt-truncate">
            {place_name}
          </h4>
        </div>
        {hovered && <DragHandle />}
        {hovered && (
          <button
            test="delete"
            className="flex-child link link--gray"
            title="Delete feature"
            onClick={this.deleteFeature}
          >
            <Icon name="trash" />
          </button>
        )}
      </div>
    );

    if (active) {
      content = (
        <div
          ref={this.activeContainerRef}
          className="flex-parent flex-parent--column bg-gray-faint relative z5 shadow-darken10 py6 px12 border-b border--gray-light"
        >
          <div className="flex-child flex-parent flex-parent--center-cross mb12">
            <div className="flex-child w30 h30 scroll-hidden round mr12 relative">
              <img src={thumbnail} />
            </div>
            <div className="flex-child flex-child--grow mr3">
              <RenameInput
                classList={'txt-m'}
                value={place_name}
                onChange={this.onChangePlaceName}
              />
            </div>
            <button
              test="delete"
              className="flex-child link link--gray"
              title="Delete feature"
              onClick={this.deleteFeature}
            >
              <Icon name="trash" />
            </button>
          </div>
          <div className="flex-child">
            <div className="mb12">
              <div className="txt-bold txt-s mb6">Description</div>
              <textarea
                test="place-description"
                className="bg-white txt-s textarea"
                onKeyDown={this.onKeyDown}
                value={place_description || ''}
                onChange={this.onChangePlaceDescription}
                placeholder="Enter description"
              />
            </div>
            <div className="mb12">
              <div className="txt-bold txt-s mb6">
                Tags<span className="ml3 txt-normal">(comma separated)</span>
              </div>
              <input
                test="tags"
                className="bg-white txt-s input"
                onKeyDown={this.onKeyDown}
                value={tags ? tags.join(', ') : ''}
                onChange={this.onChangeTags}
                placeholder="one, two"
              />
            </div>
          </div>
          <div className="flex-child mb24">
            <div className="flex-parent flex-parent--space-between-main mb6">
              <button
                test="toggle-additional"
                className="flex-child link link--gray txt-s"
                onClick={this.toggleAdditional}
              >
                <Icon
                  name={`${toggled ? 'caret-down' : 'caret-right'}`}
                  inline={true}
                />
                Additional fields
              </button>
              <button {...updatePositionProps}>
                <Icon name="crosshair" inline={true} />
                <span className="ml3">Update position</span>
              </button>
            </div>
            {toggled && (
              <>
                <div
                  ref={this.setMetaContainer}
                  className="bg-white round h120 txt-s border border--gray-light round px6 py6"
                />
                {errors && (
                  <div className="bg-red-faint color-red txt-s px6 py6 round border border--red-light mt6">
                    <strong>
                      <Icon name="alert" inline={true} />
                      Invalid input
                    </strong>
                    : {errors.join(', ')}
                  </div>
                )}
              </>
            )}
            <div className="mt12">{this.renderStatus()}</div>
          </div>
        </div>
      );
    }

    return <>{content}</>;
  }
}

Feature.propTypes = {
  parentContainer: PropTypes.any,
  mapResult: PropTypes.object,
  active: PropTypes.bool,
  feature: PropTypes.object.isRequired
};

export { Feature };
